<template>
  <div class="rs-form-tags-input-v2-wrapper">
    <div
      v-if="tagDuplicates.length > 0 && !allowDuplicates"
      class="mb-2 tag-validation-banner p-1 d-flex w-100"
    >
      <div class="pr-2">
        <RsIcon
          class="icon-color-error"
          icon="circle-exclamation"
          weight="fas"
        />
      </div>
      <div class="flex-fill">
        Tag(s) already exist: <strong>{{ tagDuplicates.join(', ') }}</strong>
      </div>
    </div>
    <label
      v-if="label"
      :for="id"
    >{{ label }}<sup v-if="required">*</sup></label>
    <input
      :id="id"
      ref="tagsInput"
      class="rs-form-tags-input"
      :placeholder="placeholder"
      :disabled="disabled"
    >
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from 'vue';
import Tagify from '@yaireo/tagify';

export type TVoerroTagsInputVal = { key: string, value: string };

export const formatVoerroInput = (input: TVoerroTagsInputVal) => input.value
  .split(',')
  .reduce<TVoerroTagsInputVal[]>((acc, cur) => [
  ...acc,
  ...cur.trim().length > 0 ? [{ key: cur, value: cur.trim() }] : [],
], []);

export default defineComponent({
  name: 'RsFormTagsInputV2',

  props: {
    /**
     * Main form element ID attribute
     */
    id: {
      type: String,
      default: 'rs-form-tags-input',
    },

    /**
     * Form Control label
     */
    label: {
      type: String,
    },

    /**
     * Adds styling if input is required (DOES NOT VALIDATE)
     */
    required: {
      type: Boolean,
      default: false,
    },

    /**
     * Disables input
     */
    disabled: {
      type: Boolean,
      default: false,
    },

    /**
     * NOTE: no consumer in this codebase passes a whitelist for suggestions, so this
     * has always been functionally inert (Tagify's dropdown suggestions require a
     * `whitelist` to have anything to show). Kept for prop-API backward compatibility.
     */
    typeahead: {
      type: Boolean,
      default: false,
    },

    /**
     * Applies a limit to amount of tags that can be added
     */
    limit: {
      type: Number,
      default: 0,
    },

    /**
     * Allows adding tags when spacebar is hit.
     */
    addTagsOnSpace: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows adding tags when comma key is hit.
     */
    addTagsOnComma: {
      type: Boolean,
      default: false,
    },

    /**
     * Allows adding tags when we blur the control.
     */
    addTagsOnBlur: {
      type: Boolean,
      default: false,
    },

    /**
     * Text value for input placeholder attribute
     */
    placeholder: {
      type: String,
    },

    /**
     * Existing tags must be formatted to `TVoerroTagsInputVal[]`.
     * To track changes, use the `:value` prop and the `@on-tags-changed`
     * event to get the same structure output.
     */
    value: {
      type: Array as PropType<TVoerroTagsInputVal[]>,
      default: () => [],
    },

    /**
     * If duplicate tag names are allowed.
     */
    allowDuplicates: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['on-tags-changed', 'on-search', 'on-blur', 'on-focus'],

  data() {
    return {
      tagDuplicates: [] as string[],
      tagifyInstance: null as Tagify | null,
    };
  },

  watch: {
    value: {
      deep: true,
      handler(newVal: TVoerroTagsInputVal[]) {
        if (!this.tagifyInstance) return;
        const current = (this.tagifyInstance.value ?? []).map((t: any) => t.value);
        const incoming = newVal.map((t) => t.value);
        if (JSON.stringify(current) === JSON.stringify(incoming)) return;
        this.tagifyInstance.removeAllTags();
        if (incoming.length) this.tagifyInstance.addTags(incoming);
      },
    },

    disabled(val: boolean) {
      // setDisabled(), not setReadonly(): tagify seeds `settings.disabled` from the source
      // input's attribute, and its event binding bails while that flag is set. setReadonly()
      // only ever touches `settings.readonly`, so re-enabling left the control inert.
      this.tagifyInstance?.setDisabled(val);
    },
  },

  mounted() {
    const instance = new Tagify(this.$refs.tagsInput as HTMLInputElement, {
      delimiters: this.buildDelimiters(),
      addTagOnBlur: this.addTagsOnBlur,
      maxTags: this.limit || Infinity,
      duplicates: this.allowDuplicates,
      // Keep the native input's value a plain comma-joined list (tagify's
      // default serializes to JSON, which leaks into validators/serializers
      // that read the DOM value).
      originalInputValueFormat: (values: { value: string }[]) => values.map((t) => t.value).join(','),
      // Required so origSettings.dropdown is defined; setReadonly() restores
      // dropdown.enabled from origSettings and crashes if it was never set.
      dropdown: { enabled: false },
    } as any);
    this.tagifyInstance = instance;

    if (this.value.length) {
      instance.addTags(this.value.map((t) => t.value));
    }
    if (this.disabled) {
      instance.setDisabled(true);
    }

    instance.on('change', () => this.handleInput());
    instance.on('focus', (e: any) => this.handleFocus(e.detail.originalEvent));
    instance.on('blur', (e: any) => this.handleBlur(e.detail.originalEvent));
    instance.on('input', (e: any) => this.handleSearch(e.detail.value));
    instance.on('invalid', (e: any) => {
      const dupeValue = e.detail?.data?.value;
      if (dupeValue && !this.tagDuplicates.includes(dupeValue)) {
        this.tagDuplicates = [...this.tagDuplicates, dupeValue];
      }
    });
  },

  beforeUnmount() {
    this.tagifyInstance?.destroy();
  },

  methods: {
    buildDelimiters(): string | null {
      const chars = [
        this.addTagsOnComma ? ',' : '',
        this.addTagsOnSpace ? ' ' : '',
      ].filter(Boolean);
      return chars.length ? chars.join('|') : null;
    },

    /**
     * Emits `@on-tags-changed` with the full current tag list whenever Tagify's
     * internal state changes (add, remove, edit).
     */
    handleInput() {
      const tagsForOutput = (this.tagifyInstance?.value ?? [])
        .flatMap((t: any) => formatVoerroInput({ key: t.value, value: t.value }));
      this.$emit('on-tags-changed', tagsForOutput);
    },

    /**
     * Public: moves the caret into the control.
     *
     * Tagify's visible field is a contenteditable <span>, not the source
     * <input> — that one is parked off-screen holding the comma-joined
     * serialization. Callers must not focus by tag name: typing into the
     * source input rewrites its value, which tagify re-parses back into tags.
     */
    focus(): void {
      this.tagifyInstance?.DOM?.input?.focus();
    },

    /**
     * When input search field is typed, emits `@on-search` event.
     */
    handleSearch(e: string) {
      this.$emit('on-search', e);
    },

    /**
     * When input search field is blurred, emits `@on-blur` event.
     */
    handleBlur(e: FocusEvent) {
      this.$emit('on-blur', e);
    },

    /**
     * When input search field is focused, emits `@on-focus` event.
     */
    handleFocus(e: FocusEvent) {
      this.tagDuplicates = [];
      this.$emit('on-focus', e);
    },
  },
});
</script>

<style lang="scss">
@import '@/styles/_variables.scss';

.tag-validation-banner {
  color: $global-text-color;
  justify-content: space-evenly;
  background-color: $error-light;
  border: 1px solid $error-dark;
  border-radius: $input-radius;
  width: fit-content;

  .icon-color-error {
    color: $error-dark;
  }
}

.rs-form-tags-input-v2-wrapper {
  .tagify {
    --tag-bg: #7d59e1;
    --tag-text-color: #fff;
    --tag-border-radius: 0.4em;
    --tag-pad: 0.35em 0.6em;

    border: 1px solid #dbdbdb;
    border-radius: 0.25em;
    padding: 0.5em 0.25em;

    &.tagify--focus {
      border: 1px solid #8bbafe;
      box-shadow: 0 0 0 0.2em rgba(13, 110, 253, 0.25);
      outline: 0 none;
    }

    @include tagify-tag-parity;
  }
}
</style>
